import { useCallback, useEffect, useRef, useState } from 'react'
import { Film } from 'lucide-react'
import clsx from 'clsx'
import { videoApi } from '../../services/api'
import type { PlaylistItem } from '../../types'

const PREVIEW_POINTS = [0.18, 0.38, 0.58, 0.78]

// A playlist can render the same video in the library, queue, and saved
// playlists. Share the signed-url request instead of asking the API each time.
const streamUrlCache = new Map<string, { url: string; expiresAt: number }>()
const streamUrlRequests = new Map<string, Promise<string>>()

function getPreviewUrl(item: PlaylistItem): Promise<string> {
  const key = `${item.provider}:${item.id}`
  const cached = streamUrlCache.get(key)
  if (cached && cached.expiresAt > Date.now() + 60_000) return Promise.resolve(cached.url)

  const pending = streamUrlRequests.get(key)
  if (pending) return pending

  const request = videoApi.getStreamUrl(item.id, item.provider)
    .then((result) => {
      streamUrlCache.set(key, result)
      return result.url
    })
    .finally(() => streamUrlRequests.delete(key))
  streamUrlRequests.set(key, request)
  return request
}

function previewTime(duration: number, point: number) {
  // Prefer a representative frame well after intros/fades. For very short
  // clips, remain close to the requested percentage without seeking past EOF.
  return Math.min(Math.max(4, duration * point), Math.max(0.1, duration - 0.25))
}

export function VideoThumbnail({
  item,
  compact = false,
  large = false,
}: {
  item: PlaylistItem
  compact?: boolean
  large?: boolean
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hoverTimerRef = useRef<number | null>(null)
  const previewIndexRef = useRef(0)
  const isHoveringRef = useRef(false)
  const [providerFailed, setProviderFailed] = useState(false)
  const [providerLoaded, setProviderLoaded] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const [shouldLoadPreview, setShouldLoadPreview] = useState(false)

  const stopPreview = useCallback(() => {
    isHoveringRef.current = false
    if (hoverTimerRef.current !== null) window.clearInterval(hoverTimerRef.current)
    hoverTimerRef.current = null
    previewIndexRef.current = 0
    const video = videoRef.current
    if (video?.duration) video.currentTime = previewTime(video.duration, PREVIEW_POINTS[0])
  }, [])

  const startPreview = useCallback(() => {
    isHoveringRef.current = true
    setShouldLoadPreview(true)
    const video = videoRef.current
    if (!video?.duration || hoverTimerRef.current !== null) return
    previewIndexRef.current = 1
    video.currentTime = previewTime(video.duration, PREVIEW_POINTS[previewIndexRef.current])
    hoverTimerRef.current = window.setInterval(() => {
      const currentVideo = videoRef.current
      if (!currentVideo?.duration) return
      previewIndexRef.current = (previewIndexRef.current + 1) % PREVIEW_POINTS.length
      currentVideo.currentTime = previewTime(currentVideo.duration, PREVIEW_POINTS[previewIndexRef.current])
    }, 900)
  }, [])

  useEffect(() => {
    setProviderFailed(false)
    setProviderLoaded(false)
    setVideoReady(false)
    setShouldLoadPreview(false)
    stopPreview()
  }, [item.id, item.provider, stopPreview])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (!('IntersectionObserver' in window)) {
      setShouldLoadPreview(true)
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShouldLoadPreview(true)
        observer.disconnect()
      }
    }, { rootMargin: '160px' })
    observer.observe(root)
    return () => observer.disconnect()
  }, [item.id])

  useEffect(() => {
    if (!shouldLoadPreview) return
    let cancelled = false
    void getPreviewUrl(item).then((url) => {
      if (!cancelled && videoRef.current) videoRef.current.src = url
    }).catch(() => {
      // Keep the provider thumbnail (or icon) when video previewing is blocked.
    })
    return () => {
      cancelled = true
      stopPreview()
      const video = videoRef.current
      if (video) {
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [item, shouldLoadPreview, stopPreview])

  const showProviderImage = Boolean(item.thumbnailUrl) && !providerFailed && !videoReady

  return (
    <div
      ref={rootRef}
      className={clsx(
        'relative z-0 flex flex-shrink-0 origin-left items-center justify-center overflow-hidden rounded bg-border/70 text-muted',
        !large && 'transition-[transform,box-shadow] duration-200 ease-out hover:z-50 hover:scale-[1.8] hover:shadow-2xl hover:ring-1 hover:ring-white/15',
        !large && 'focus-within:z-50 focus-within:scale-[1.8] focus-within:shadow-2xl',
        large ? 'h-full w-full rounded-none' : compact ? 'h-9 w-16' : 'h-12 w-20',
      )}
      aria-label={`${item.name} thumbnail; hover to preview`}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      onFocus={startPreview}
      onBlur={stopPreview}
    >
      {showProviderImage && (
        <img
          src={item.thumbnailUrl!}
          alt=""
          loading="lazy"
          className={clsx('absolute inset-0 h-full w-full object-cover transition-opacity', providerLoaded ? 'opacity-100' : 'opacity-0')}
          onLoad={() => setProviderLoaded(true)}
          onError={() => setProviderFailed(true)}
        />
      )}
      {shouldLoadPreview && (
        <video
          ref={videoRef}
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          className={clsx(
            'pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-200',
            videoReady ? 'opacity-100' : 'opacity-0',
          )}
          onLoadedMetadata={(event) => {
            event.currentTarget.currentTime = previewTime(event.currentTarget.duration, PREVIEW_POINTS[0])
            if (isHoveringRef.current) startPreview()
          }}
          onSeeked={() => setVideoReady(true)}
        />
      )}
      {(!showProviderImage || !providerLoaded) && !videoReady && (
        <Film size={compact ? 14 : 17} strokeWidth={1.5} />
      )}
    </div>
  )
}
